import { OpenAPI } from '@/client';
import type {
  AdminActionLogPublic,
  AdminGroupCreate,
  AdminGroupPermissionUpdate,
  AdminGroupPublic,
  AdminGroupUpdate,
  AdminLoginResponse,
  AdminMfaChallengeRequest,
  AdminPermissionPublic,
  AdminUserGroupUpdate,
  Message,
  UpdatePassword,
  UserCreate,
  UserPublic,
  UsersPublic,
  UserUpdate,
  UserUpdateMe,
} from '@/client/types.gen';
import { type AdminTenant, getAdminTenant } from '@/utils/adminTenant';
import type { PermissionSyncItem } from '@/utils/permissionCatalog';
import { getToken } from '@/utils/token';

export type Pagination<T> = {
  data: T[];
  count: number;
  skip?: number;
  page_size?: number;
};

export type AdminTenantRecord = AdminTenant & {
  status: 'enabled' | 'disabled';
  created_at: string;
  updated_at: string;
};

export type TenantMediaConfig = {
  s3_driver: string;
  s3_bucket: string;
  s3_region: string;
  s3_endpoint?: string | null;
  s3_public_endpoint?: string | null;
  s3_access_key_id?: string | null;
  s3_secret_access_key?: string | null;
  google_drive_service_account_json?: Record<string, unknown> | null;
  cdn_hosts: string[];
  backup_cdn_hosts: string[];
  regional_cdn_hosts?: Record<string, string[]>;
  regional_backup_cdn_hosts?: Record<string, string[]>;
  cdn_signed_url_enabled: boolean;
  cdn_signed_url_key_name?: string | null;
  cdn_signed_url_private_key_base64?: string | null;
  cdn_signed_url_ttl_seconds: number;
  s3_credentials_configured?: boolean;
  google_drive_credentials_configured?: boolean;
  cdn_signed_url_private_key_configured?: boolean;
  bucket_configs?: TenantMediaBucketConfig[];
};

export type TenantMediaBucketConfig = {
  bucket: string;
  s3_driver: string;
  s3_region?: string | null;
  s3_endpoint?: string | null;
  s3_public_endpoint?: string | null;
  s3_access_key_id?: string | null;
  s3_secret_access_key?: string | null;
  cdn_hosts: string[];
  backup_cdn_hosts: string[];
  regional_cdn_hosts?: Record<string, string[]>;
  regional_backup_cdn_hosts?: Record<string, string[]>;
  enabled: boolean;
  s3_credentials_configured?: boolean;
};

type RequestOptions = RequestInit & {
  query?: Record<string, unknown>;
};

const buildUrl = (path: string, query?: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    search.set(key, String(value));
  });
  return `${OpenAPI.BASE}${path}${search.toString() ? `?${search.toString()}` : ''}`;
};

const requestJson = async <T>(path: string, options: RequestOptions = {}) => {
  const token = getToken();
  const response = await fetch(buildUrl(path, options.query), {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(getAdminTenant()
        ? { 'X-Admin-Tenant': getAdminTenant() as string }
        : {}),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw {
      body: payload,
      message:
        (payload as { detail?: string; message?: string }).detail ||
        (payload as { detail?: string; message?: string }).message ||
        '请求失败',
    };
  }
  return payload as T;
};

export const adminAuthFacade = {
  loginAccessToken(values: { username: string; password: string }) {
    const form = new URLSearchParams();
    form.set('username', values.username);
    form.set('password', values.password);
    return requestJson<AdminLoginResponse>('/api/v2/login/access-token', {
      method: 'POST',
      body: form,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },

  confirmMfaSetup(body: AdminMfaChallengeRequest) {
    return requestJson<AdminLoginResponse>('/api/v2/login/mfa/setup/confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  verifyMfaLogin(body: AdminMfaChallengeRequest) {
    return requestJson<AdminLoginResponse>('/api/v2/login/mfa/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  listTenants() {
    return requestJson<AdminTenant[]>('/api/v2/tenants');
  },

  listTenantRecords() {
    return requestJson<AdminTenantRecord[]>('/api/v2/tenants', {
      query: { include_disabled: true },
    });
  },

  createTenant(body: {
    code: string;
    name: string;
    database_name: string;
    status: 'enabled' | 'disabled';
  }) {
    return requestJson<AdminTenantRecord>('/api/v2/tenants', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateTenant(
    tenantId: number,
    body: {
      code: string;
      name: string;
      database_name: string;
      status: 'enabled' | 'disabled';
    },
  ) {
    return requestJson<AdminTenantRecord>(`/api/v2/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deleteTenant(tenantId: number) {
    return requestJson<void>(`/api/v2/tenants/${tenantId}`, {
      method: 'DELETE',
    });
  },

  getTenantMediaConfig(tenantId: number) {
    return requestJson<TenantMediaConfig>(
      `/api/v2/tenants/${tenantId}/media-config`,
    );
  },

  updateTenantMediaConfig(tenantId: number, body: TenantMediaConfig) {
    return requestJson<TenantMediaConfig>(
      `/api/v2/tenants/${tenantId}/media-config`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );
  },
};

export const adminUsersFacade = {
  readUsers(params: { skip?: string | number; limit?: string | number } = {}) {
    return requestJson<UsersPublic>('/api/v2/users', { query: params });
  },

  createUser(body: UserCreate & { mfa_required?: boolean }) {
    return requestJson<UserPublic>('/api/v2/users', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  readUserMe() {
    return requestJson<UserPublic>('/api/v2/users/me');
  },

  updateUserMe(body: UserUpdateMe & { mfa_required?: boolean }) {
    return requestJson<UserPublic>('/api/v2/users/me', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  updatePasswordMe(body: UpdatePassword) {
    return requestJson<Message>('/api/v2/users/me/password', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  updateUser(userId: string, body: UserUpdate & { mfa_required?: boolean }) {
    return requestJson<UserPublic>(`/api/v2/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  deleteUser(userId: string) {
    return requestJson<Message>(`/api/v2/users/${userId}`, {
      method: 'DELETE',
    });
  },

  readUserTenantAccess(userId: string) {
    return requestJson<{ tenant_ids: number[] }>(
      `/api/v2/users/${userId}/tenant-access`,
    );
  },

  updateUserTenantAccess(userId: string, tenantIds: number[]) {
    return requestJson<{ tenant_ids: number[] }>(
      `/api/v2/users/${userId}/tenant-access`,
      {
        method: 'PUT',
        body: JSON.stringify({ tenant_ids: tenantIds }),
      },
    );
  },

  resetUserMfaBinding(userId: string) {
    return requestJson<Message>(`/api/v2/users/${userId}/mfa/reset`, {
      method: 'POST',
    });
  },
};

export const adminRbacFacade = {
  listPermissions() {
    return requestJson<AdminPermissionPublic[]>('/api/v2/rbac/permissions');
  },

  syncPermissions(permissions: PermissionSyncItem[] = []) {
    return requestJson<Message>('/api/v2/rbac/permissions/sync', {
      method: 'POST',
      body: JSON.stringify({ permissions }),
    });
  },

  listGroups(
    params: {
      skip?: number;
      limit?: number;
      keyword?: string;
      isActive?: boolean;
    } = {},
  ) {
    return requestJson<Pagination<AdminGroupPublic>>('/api/v2/rbac/groups', {
      query: {
        skip: params.skip,
        limit: params.limit,
        keyword: params.keyword,
        is_active: params.isActive,
      },
    });
  },

  createGroup(body: AdminGroupCreate) {
    return requestJson<AdminGroupPublic>('/api/v2/rbac/groups', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  getGroup(groupId: number) {
    return requestJson<AdminGroupPublic>(`/api/v2/rbac/groups/${groupId}`);
  },

  updateGroup(groupId: number, body: AdminGroupUpdate) {
    return requestJson<AdminGroupPublic>(`/api/v2/rbac/groups/${groupId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  deleteGroup(groupId: number) {
    return requestJson<Message>(`/api/v2/rbac/groups/${groupId}`, {
      method: 'DELETE',
    });
  },

  updateGroupPermissions(groupId: number, body: AdminGroupPermissionUpdate) {
    return requestJson<AdminGroupPublic>(
      `/api/v2/rbac/groups/${groupId}/permissions`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );
  },

  updateUserGroups(userId: string, body: AdminUserGroupUpdate) {
    return requestJson(`/api/v2/rbac/users/${userId}/groups`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  listActionLogs(
    params: {
      skip?: number;
      limit?: number;
      actorEmail?: string;
      resourceType?: string;
      method?: string;
      success?: boolean;
      keyword?: string;
      createdAtStart?: string;
      createdAtEnd?: string;
    } = {},
  ) {
    return requestJson<Pagination<AdminActionLogPublic>>(
      '/api/v2/rbac/action-logs',
      {
        query: {
          skip: params.skip,
          limit: params.limit,
          actor_email: params.actorEmail,
          resource_type: params.resourceType,
          method: params.method,
          success: params.success,
          keyword: params.keyword,
          created_at_start: params.createdAtStart,
          created_at_end: params.createdAtEnd,
        },
      },
    );
  },
};
